
import { LogicRule } from "@/lib/logic-rules";
import type { PuzzleData } from "@/lib/types/puzzle-types";

/**
 * Process row data into words, rule name, and reasoning
 */
export function processRowData(
  row: string[], 
  wordIndex: number, 
  ruleIndex: number, 
  reasoningIndex: number,
  spectrumIndex: number = -1,
  linkIndices: number[] = []
): { words: string[], ruleName: string, reasoning: string, spectrum?: [string, string], wordLinks?: string[] } {
  // Extract words, rule, and reasoning directly from the spreadsheet row
  const wordsString = row[wordIndex] || '';
  const ruleName = row[ruleIndex] || '';
  // Get reasoning, ensuring it's treated as a string
  const reasoning = reasoningIndex !== -1 ? String(row[reasoningIndex] || '') : '';
  
  const words = wordsString.split(',').map((w: string) => w.trim());
  
  if (!ruleName) {
    throw new Error("No logic rule name provided in spreadsheet");
  }
  
  if (words.length < 2) {
    throw new Error("Not enough words provided in spreadsheet");
  }
  
  // Extract spectrum from the dedicated column if available
  let spectrum: [string, string] | undefined;
  
  if (spectrumIndex !== -1 && row[spectrumIndex]) {
    const spectrumString = row[spectrumIndex];
    // Check if spectrum contains "to" and split it
    if (spectrumString.toLowerCase().includes(' to ')) {
      const parts = spectrumString.split(/ to /i);
      if (parts.length === 2) {
        spectrum = [parts[0].trim(), parts[1].trim()];
        console.log('Extracted spectrum from dedicated column:', spectrum);
      }
    }
  } 
  // Fall back to parsing from rule name if needed
  else if (ruleName.toLowerCase().includes(' to ')) {
    // Split by "to"
    const nameParts = ruleName.split(/ to /i);
    if (nameParts.length === 2) {
      // For the first part, get the last word which is the first spectrum label
      const firstPartWords = nameParts[0].trim().split(' ');
      if (firstPartWords.length > 1) {
        const firstLabel = firstPartWords[firstPartWords.length - 1];
        const secondLabel = nameParts[1].trim();
        spectrum = [firstLabel, secondLabel];
        console.log('Extracted spectrum from rule name as fallback:', spectrum);
      }
    }
  }
  
  // Extract links from columns F-J (or other identified link columns)
  let wordLinks: string[] | undefined;
  
  // FORCED DEBUG LOGGING for link detection
  console.log('FORCED DEBUG - Processing links:', {
    linkIndices,
    wordsCount: words.length,
    rowData: row.slice(0, 10), // Show first 10 items for debugging
  });
  
  if (linkIndices.length > 0) {
    // Initialize the wordLinks array with empty strings for each word
    wordLinks = new Array(words.length).fill('');
    
    // Process each link column that we found
    linkIndices.forEach((linkIndex, i) => {
      // Only process if we have a corresponding word
      if (i < words.length && linkIndex < row.length) {
        const link = row[linkIndex];
        if (link && link.trim()) {
          console.log(`FORCED DEBUG - Found link for word ${i} (${words[i]}): ${link}`);
          wordLinks![i] = link.trim();
        }
      }
    });
    
    // If we have no actual links, set wordLinks to undefined
    if (wordLinks.every(link => !link)) {
      console.log('FORCED DEBUG - No valid links found in any columns');
      wordLinks = undefined;
    } else {
      console.log('FORCED DEBUG - Final word links array:', wordLinks);
    }
  }
  
  return { words, ruleName, reasoning, spectrum, wordLinks };
}

/**
 * Create a LogicRule object from spreadsheet data
 */
export function createLogicRuleFromSpreadsheet(
  ruleName: string,
  reasoning: string,
  spectrum?: [string, string]
): LogicRule {
  // Extract the base rule name (e.g., "Weight" from "Weight Heavy to Light")
  let baseRuleName = ruleName;
  
  // If ruleName contains spectrum info, extract just the base name
  if (ruleName.toLowerCase().includes(' to ')) {
    const nameParts = ruleName.split(' ');
    // Assume the base name is before any " to " parts
    const toIndex = nameParts.findIndex(part => part.toLowerCase() === 'to');
    if (toIndex > 0) {
      baseRuleName = nameParts.slice(0, toIndex-1).join(' ');
    }
  }
  
  // Generate a unique ID for this rule
  const id = `spreadsheet_${baseRuleName.toLowerCase().replace(/\s+/g, '_')}`;
  
  // Create a simple sort function that preserves the order from the spreadsheet
  const sortFn = (a: string, b: string) => 0; // No-op sort function
  
  console.log('Creating rule with values:', {
    id,
    name: baseRuleName, 
    spectrum,
    hasSpectrum: !!spectrum,
    reasoning: reasoning ? (reasoning.substring(0, 50) + '...') : 'No reasoning provided'
  });
  
  // Create the rule object
  const rule: LogicRule = {
    id,
    name: baseRuleName, // Use just the base name (e.g., "Weight")
    description: '', // We don't use description anymore, keep it empty
    sortFn,
    category: "spreadsheet",
    reasoning,
    spectrum // Add the spectrum to the rule object
  };
  
  console.log('Created rule from spreadsheet:', rule);
  return rule;
}

/**
 * Create puzzle data from processed row data
 */
export function createPuzzleData(
  words: string[],
  ruleName: string,
  reasoning: string,
  spectrum?: [string, string],
  wordLinks?: string[]
): PuzzleData {
  // Create a rule object from the spreadsheet data
  const rule = createLogicRuleFromSpreadsheet(ruleName, reasoning, spectrum);
  
  // CRITICAL: Use the words as they appear in the spreadsheet - do NOT reorder them
  const correctOrder = [...words];
  
  // FORCED DEBUG for puzzle creation with links
  console.log('FORCED DEBUG - Creating puzzle with data:', {
    words, 
    ruleName,
    spectrum: spectrum ? spectrum.join(' to ') : 'None',
    wordLinks,
    hasWordLinks: !!wordLinks,
    linksCount: wordLinks?.length,
    wordLinksContent: wordLinks,
    wordsCount: words.length
  });
  
  // Create and return the puzzle object
  return {
    words,
    correctRule: rule,
    correctOrder,
    logicRules: [rule],
    date: new Date().toISOString(),
    wordLinks // Add wordLinks to puzzle data
  };
}
